import type { PaymentActivityDetails } from '@paintscout/api'
import { useActivityQuery, useDeletePaymentMutation, useUpdatePaymentMutation } from '@paintscout/api'
import PaymentStatusBadge from '../../Quote/views/QuoteView/PaymentsTable/PaymentStatusBadge'
import type { DialogProps } from '@paintscout/ui'
import {
  Button,
  ClickAwayListener,
  ConfirmationDialog,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputField,
  makeStyles,
  Spinner,
  Typography,
  useTranslation
} from '@paintscout/ui'
import { useDialogs, useClientOptions, useUsers } from '@paintscout/ui-business'
import { removeAuth0Prefix, getPaymentPortalLink } from '@paintscout/util'
import React, { useCallback, useState } from 'react'
import { canRefundPayment, formatCurrency, getPaymentProviderLabel, setPayments } from '@paintscout/util/builder'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import type { Payment, QuoteDocument } from 'paintscout'
import * as Sentry from '@sentry/react'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import DownloadIcon from '@material-ui/icons/GetApp'
import EditIcon from '@material-ui/icons/Edit'
import UndoIcon from '@material-ui/icons/Undo'
import LaunchIcon from '@material-ui/icons/Launch'
import { StripeWordmark } from '@paintscout/ui/icons'

export interface ViewPaymentDialogProps extends DialogProps {
  activityId?: string
  payment?: Payment
  quote: QuoteDocument
  onClose?: () => void
  onDelete?: () => void
  readonly?: boolean
  onReceipt?: (payment: Payment) => void
  onRefund?: (payment: Payment) => void
  onSaveNote?: (updatedQuote: QuoteDocument) => void
  onQuoteUpdated?: (updatedQuote: QuoteDocument) => void
  inPresentation?: boolean
  /** #18623 flag — passed by the opener (this dialog renders outside the LaunchDarkly context). */
}

const useStyles = makeStyles((theme) => ({
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  fieldLabel: {
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(3)
  },
  noteEditRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  noteRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    '& svg': {
      color: theme.paintscout.palette.brand.Neutral.P70,
      position: 'relative',
      left: 3,
      bottom: -1
    },
    cursor: 'pointer',
    '&:hover svg': {
      color: theme.paintscout.palette.brand.Neutral.P60
    }
  },
  stripeWordmark: {}, // empty but required for below
  stripeButton: {
    // the Stripe wordmark is purple by default; flip it to white on hover (when the button fills in)
    '&:hover $stripeWordmark': {
      fill: '#fff'
    }
  }
}))

const ViewPaymentDialog = (props: ViewPaymentDialogProps) => {
  const {
    open,
    activityId,
    payment: paymentProp,
    onClose,
    onDelete,
    readonly,
    quote,
    onReceipt,
    onRefund,
    onSaveNote,
    onQuoteUpdated,
    inPresentation,
    ...baseDialogProps
  } = props
  const { options } = useClientOptions()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { users } = useUsers()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles()
  const { __ } = useTranslation()

  const [payment, setPayment] = useState<Payment>(paymentProp)
  const [noteValue, setNoteValue] = useState(paymentProp?.note ?? '')
  const [editingNote, setEditingNote] = useState(false)

  const [deletePaymentMutation] = useDeletePaymentMutation()
  const [updatePaymentMutation, { loading: savingNote }] = useUpdatePaymentMutation()

  const handleError = (error: any) => {
    Sentry.captureException(error)
    enqueueSnackbar(__('Unable to fetch payment activity'), { variant: 'error' })
    dismissDialog()
  }

  const { loading, data } = useActivityQuery({
    skip: !activityId || inPresentation,
    fetchPolicy: 'network-only',
    pollInterval: 120_000,
    notifyOnNetworkStatusChange: false,
    variables: { id: activityId },
    onCompleted: useCallback((data) => {
      if (!data.activity) {
        handleError(new Error(`Unable to fetch payment activity with id: ${activityId}`))
      } else {
        // Prefer the quote's payment record, but fall back to the activity details so the dialog
        // can still render when no matching payment exists on the quote.
        const found = quote.payments?.find((p) => p.activityId === activityId)
        const resolved = found ?? paymentFromActivity(activityId, data.activity.details as PaymentActivityDetails)
        setPayment(resolved)
        setNoteValue(resolved?.note ?? '')
      }
    }, []),
    onError: useCallback((error) => {
      handleError(error)
    }, [])
  })

  const activity = data?.activity
  const isReady = !!payment && (inPresentation || !activityId || !!activity)

  function handleClose() {
    if (onClose) {
      onClose()
    } else {
      dismissDialog()
    }
  }

  async function handleSaveNote() {
    if (onSaveNote) {
      const payments = quote.payments?.map((p) => (p.id === payment.id ? { ...p, note: noteValue } : p))
      const updatedQuote = setPayments({ quote, payments, options })
      setPayment((prev) => ({ ...prev, note: noteValue }))
      onSaveNote(updatedQuote)
      setEditingNote(false)
    } else {
      try {
        const result = await updatePaymentMutation({
          variables: { quoteId: quote._id, paymentId: payment.id, note: noteValue }
        })
        const updatedQuote = result?.data?.updatePayment?.updatedQuote as QuoteDocument
        if (updatedQuote) {
          setPayment((prev) => ({ ...prev, note: noteValue }))
          setEditingNote(false)
          onQuoteUpdated?.(updatedQuote)
        }
      } catch (error) {
        Sentry.captureException(error)
        enqueueSnackbar(__('Unable to save note'), { variant: 'error' })
      }
    }
  }

  function handleMutationDelete() {
    openDialog(ConfirmationDialog, {
      message: __('Are you sure you want to remove this payment?'),
      closeButton: true,
      noLabel: __('Back'),
      onNoLabelClick: dismissDialog,
      yesLabel: __("Yes, I'm Sure"),
      onConfirm: async () => {
        dismissDialog()
        try {
          const result = await deletePaymentMutation({
            variables: { quoteId: quote._id, paymentId: payment.id }
          })
          const updatedQuote = result?.data?.deletePayment?.updatedQuote as QuoteDocument
          if (updatedQuote) {
            onQuoteUpdated?.(updatedQuote)
            handleClose()
          }
        } catch (error) {
          Sentry.captureException(error)
          enqueueSnackbar(__('Unable to delete payment'), { variant: 'error' })
        }
      },
      onCancel: dismissAllDialogs
    })
  }

  const activityOwner = (activity?.details as PaymentActivityDetails)?.owner
  const createdByUser =
    !activityOwner && payment?.createdBy
      ? users?.find((u) => removeAuth0Prefix(u.user_id) === removeAuth0Prefix(payment.createdBy))
      : null
  const owner = inPresentation
    ? null
    : activityOwner ||
      (createdByUser
        ? {
            id: removeAuth0Prefix(createdByUser.user_id),
            firstName: createdByUser.user_metadata?.firstName ?? '',
            lastName: createdByUser.user_metadata?.lastName ?? ''
          }
        : null) ||
      quote.owner

  const filteredEvents = (activity?.details?.events || []).filter((event) => {
    return event !== 'fully paid' && !event.includes('still owing')
  })

  // The payment record's providerDetails can be missing (e.g. payments processed by an older
  // deploy), so fall back to the full activity, which stores the raw provider details.
  const activityDetails = activity?.details as PaymentActivityDetails | undefined
  const providerDetails = payment?.providerDetails ?? activityDetails?.providerDetails
  const provider = payment?.provider ?? activityDetails?.provider
  const portalLink = inPresentation ? null : getPaymentPortalLink(providerDetails, options)
  const isStripePayment = provider === 'stripe' || provider === 'stripeW1d'
  const isCanadianPayrix = payment?.provider === 'payrix' && payment?.providerDetails?.payrix?.isCanadian
  const paymentMethod = (activity?.details as PaymentActivityDetails)?.method ?? payment?.type ?? ''

  const achLabel = isCanadianPayrix ? 'EFT' : 'ACH'
  const originalPayment = payment?.forPaymentId ? quote.payments?.find((p) => p.id === payment.forPaymentId) : null
  const bannerMessage =
    payment?.status === 'paid'
      ? __('Successful.')
      : payment?.status === 'refunded'
        ? originalPayment
          ? __(
              '%s of %s was refunded.',
              formatCurrency({ options, value: Math.abs(payment.amount) }),
              formatCurrency({ options, value: originalPayment.amount })
            )
          : __('%s was refunded.', formatCurrency({ options, value: Math.abs(payment.amount) }))
        : payment?.status === 'processing'
          ? ['ach', 'eft'].includes(paymentMethod?.toLowerCase())
            ? __('%s payments usually take up to 5 business days to resolve.', achLabel)
            : __('This payment should resolve within a few hours.')
          : payment?.status === 'failed'
            ? __('Payment failed.') + (filteredEvents.length > 0 ? ` - ${filteredEvents.join(', ')}.` : '')
            : null
  const paymentMethodWithEFT = isCanadianPayrix && paymentMethod.toLowerCase() === 'ach' ? 'EFT' : paymentMethod
  const providerLabel = payment && !inPresentation ? getPaymentProviderLabel(payment) : null
  const hasSurcharge = !!payment?.surcharge && payment.surcharge > 0
  const baseAmount = payment ? payment.amount - (payment.surcharge || 0) : 0
  const isNoteDirty = payment ? noteValue !== (payment.note ?? '') : false

  const isProcessedPayment = !!payment?.activityId && !payment?.manual
  // A synthesized fallback payment is built from the activity when the quote has no matching payment
  // record (see paymentFromActivity). It has no id, so it can't be the target of any mutation (save
  // note, refund, delete) nor handed to a callback that expects a real payment. Gate every
  // interactive action on it — only the read-only fields and the external portal link stay enabled.
  const isSynthesizedPayment = !!payment && !payment.id
  // Delete and refund are payment-improvements features (deletePayment/refundPayment), gated by
  // the flag (passed as a prop from the opener) — the backend resolvers throw ForbiddenError when
  // it's off. Note editing predates the flag, so it stays available regardless — only
  // synthesized/in-presentation/read-only payments can't be edited.
  const canEditNote = !inPresentation && !isSynthesizedPayment && (!!onSaveNote || (!readonly && !!quote?._id))
  const showDeleteButton =
    (!!onDelete || (!readonly && !isProcessedPayment)) && !inPresentation && !isSynthesizedPayment && isReady

  return (
    <Dialog open={open} maxWidth="sm" {...baseDialogProps}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        }
      >
        {__('Payment Details')}
      </DialogTitle>
      <DialogContent>
        {(loading || !isReady) && <Spinner />}
        {isReady && (
          <>
            {bannerMessage && <PaymentStatusBadge status={payment.status} message={bannerMessage} />}
            <div className={classes.fields} style={bannerMessage ? { marginTop: 16 } : undefined}>
              {owner && (
                <PaymentField
                  label={__('Added By')}
                  value={`${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim()}
                />
              )}
              <PaymentField
                label={hasSurcharge ? __('Subtotal') : __('Amount')}
                value={formatCurrency({ options, value: baseAmount })}
              />
              {hasSurcharge && (
                <>
                  <PaymentField
                    label={__('Credit Card Surcharge')}
                    value={formatCurrency({ options, value: payment.surcharge })}
                  />
                  <PaymentField label={__('Total')} value={formatCurrency({ options, value: payment.amount })} />
                </>
              )}
              {!!paymentMethodWithEFT && (
                <PaymentField
                  label={__('Method')}
                  value={providerLabel ? `${paymentMethodWithEFT} (${providerLabel})` : paymentMethodWithEFT}
                />
              )}
              <PaymentField label={__('Date')} value={moment(payment.date).format('MMMM D, YYYY, h:mm a')} />
              {payment.deposit && <PaymentField label={__('Type')} value={__('Deposit')} />}
              {canEditNote ? (
                editingNote ? (
                  <ClickAwayListener
                    onClickAway={() => {
                      if (!isNoteDirty) {
                        setEditingNote(false)
                      }
                    }}
                  >
                    <div>
                      <Typography variant="caption" display="block" className={classes.fieldLabel}>
                        {__('Client Note')}
                      </Typography>
                      <div className={classes.noteEditRow}>
                        <InputField
                          value={noteValue}
                          fullWidth
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNoteValue(e.target.value)}
                        />
                        <IconButton disabled={!isNoteDirty || savingNote} loading={savingNote} onClick={handleSaveNote}>
                          <CheckIcon />
                        </IconButton>
                      </div>
                    </div>
                  </ClickAwayListener>
                ) : (
                  <div className={classes.noteRow} onClick={() => setEditingNote(true)}>
                    <PaymentField
                      label={__('Client Note')}
                      value={noteValue || ''}
                      icon={<EditIcon style={{ fontSize: '1rem' }} />}
                    />
                  </div>
                )
              ) : (
                !!payment.note && (
                  <PaymentField label={inPresentation ? __('Note') : __('Client Note')} value={payment.note} />
                )
              )}
            </div>
            <div className={classes.actions}>
              {onReceipt && !isSynthesizedPayment && payment.status !== 'processing' && (
                <Button variant={'dialog'} icon={DownloadIcon} onClick={() => onReceipt(payment)}>
                  {__('Receipt')}
                </Button>
              )}
              {onRefund && !isSynthesizedPayment && canRefundPayment(payment, quote.payments ?? []) && (
                <Button variant={'dialog'} icon={UndoIcon} onClick={() => onRefund(payment)}>
                  {__('Refund')}
                </Button>
              )}
              {!!portalLink && (
                <Button
                  variant={'dialog'}
                  icon={LaunchIcon}
                  className={isStripePayment ? classes.stripeButton : undefined}
                  onClick={() => window.open(portalLink, '_blank', 'noopener,noreferrer')}
                >
                  {isStripePayment ? (
                    <StripeWordmark
                      className={classes.stripeWordmark}
                      style={{ height: '1.4em', width: 'auto', display: 'block' }}
                    />
                  ) : (
                    __('Portal')
                  )}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
      {showDeleteButton && (
        <DialogActions
          leftButton={
            <Button variant={'textNoPadding'} onClick={onDelete ?? handleMutationDelete}>
              {__('Delete')}
            </Button>
          }
        />
      )}
    </Dialog>
  )
}

export default ViewPaymentDialog

function parseAmount(value?: string | number | null): number {
  if (value == null) {
    return 0
  }
  const num = typeof value === 'number' ? value : Number.parseFloat(String(value).replaceAll(/[^0-9.-]/g, ''))
  return Number.isNaN(num) ? 0 : num
}

/**
 * Build a best-effort Payment from a payment activity's details. Used as a fallback when the quote
 * has no matching payment record for the activityId (e.g. older quotes, deals, or a payment that was
 * removed) so the dialog can still render whatever the activity knows about the payment.
 */
function paymentFromActivity(activityId: string, details?: PaymentActivityDetails): Payment | undefined {
  if (!details) {
    return undefined
  }
  const status: Payment['status'] = details.isFailed
    ? 'failed'
    : details.isRefund
      ? 'refunded'
      : details.isProcessing
        ? 'processing'
        : 'paid'
  const surcharge = parseAmount(details.surcharge)
  return {
    activityId,
    amount: parseAmount(details.amountPaid),
    surcharge: surcharge > 0 ? surcharge : undefined,
    date: details.paymentDate ?? details.timestamp,
    note: details.note ?? '',
    deposit: !!details.deposit,
    type: details.method ?? '',
    provider: details.provider as Payment['provider'],
    providerDetails: details.providerDetails,
    status
  }
}

function PaymentField({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  const classes = useStyles()
  return (
    <div>
      <Typography variant="caption" display="block" className={classes.fieldLabel}>
        {label}
        {icon && <span>{icon}</span>}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </div>
  )
}
